import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * F5-T2 · El patrón de perfil/ajustes con pestañas (mockup «Agente 04», v.6).
 *
 * «Una familia con pestañas, estado por etiqueta y no por color de fondo. Es
 * el mismo archivo del perfil del admin con otro contenido en la tercera
 * pestaña.» Reglas del dibujo: un primario por pestaña · lo no-editable se
 * muestra SIN campo, con la razón al lado · las advertencias van EN el punto
 * de acción, no arriba de la página.
 */
export function ProfileTabs({ tabs, active, onTab }: {
  tabs: Array<{ id: string; label: string }>;
  active: string;
  onTab: (id: string) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1 border-b border-nav-line" role="tablist" data-chassis="profile-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          onClick={() => onTab(tab.id)}
          className={cn(
            'min-h-[44px] px-4 font-body text-[13.5px] border-b-2 -mb-px transition-colors',
            active === tab.id
              ? 'border-nav-teal text-nav-navy font-semibold'
              : 'border-transparent text-nav-slate hover:text-nav-navy',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/** Estado por ETIQUETA, no por color de fondo: un chip gris junto al dato. */
export function StateChip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-nav-soft text-nav-slate text-[11.5px] font-medium" data-chassis="state-chip">
      {children}
    </span>
  );
}

/** Dato no-editable: se muestra sin campo, con la razón al lado. */
export function ReadonlyRow({ label, value, reason }: {
  label: string;
  value: ReactNode;
  reason?: string;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 py-2" data-chassis="readonly-row">
      <span className="text-[13px] text-nav-slate shrink-0">{label}</span>
      <span className="text-[13.5px] text-nav-navy text-right">
        {value}
        {reason ? <span className="block text-[11.5px] text-nav-faint">{reason}</span> : null}
      </span>
    </div>
  );
}
